'use client';

import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { faCircleInfo, faPlus } from '@fortawesome/pro-light-svg-icons';
import { AnimatePresence, motion } from 'framer-motion';
import { useStore } from 'zustand';
import { useRouter } from 'next/navigation';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { DiscardChangesModal } from '@/app/(main)/(pages)/(auth)/dependentes/_components/discard-changes-modal.component';
import { Button } from '@/components/ui/button/button.component';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.component';
import { Input } from '@/components/ui/input/input.component';
import { useToast } from '@/components/ui/use-toast';
import { Icon } from '@/components/ui/icon.component';
import { cpfValidator } from '@/utils/validators';
import { completeNameValidator } from '@/utils/validators/complete-name.validator';
import { datePastValidator } from '@/utils/validators/date.validator';
import { isDependentCpfDuplicate } from '@/utils/validators/dependent-cpf.validator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group.component';
import { Dependent, DocumentType, Gender } from '@/types/auth-base.types';
import { checkIfCpfExists } from '@/services/auth/registration.service';
import { authStore, resolveAuthenticatedUser } from '@/store/auth.store';
import { useLoading } from '@/hooks/use-loading.hook';
import { formatBrDateToISO8601 } from '@/utils/formatters/format-br-date-to-iso-8601';
import { HttpException } from '@/utils/api/api-client';
import { formatISO8601toBRDate } from '@/utils/formatters/format-iso-8601-to-br-date';
import { updateDependent } from '@/services/dependent/update-dependent.service';
import { createDependent } from '@/services/dependent/create-dependent.service';
import { Tooltip } from '@/components/ui/tooltip.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { useInterceptNavigation } from '@/hooks/use-intercept-navigation.hook';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

export const createDependentFormSchemaFactory = (cpfExists: boolean, cpfDuplicateInDependents: boolean) => z.object({
  legalName: z
    .string()
    .min(1, { message: 'Informe o nome completo.' })
    .max(130, { message: 'O nome deve ter no máximo 130 caracteres.' })
    .refine(completeNameValidator, {
      message: 'Informe o nome completo.',
    }),
  socialName: z
    .string()
    .max(130, { message: 'O nome social deve ter no máximo 130 caracteres.' })
    .optional(),
  birthday: z
    .string()
    .min(1, { message: 'Informe a data de nascimento.' })
    .refine(datePastValidator, {
      message: 'Informe uma data de nascimento válida.',
    }),
  documentNumber: z
    .string()
    .min(1, { message: 'Informe o CPF.' })
    .refine(cpfValidator, {
      message: 'Informe um CPF válido.',
    })
    .refine(() => !cpfExists, {
      message: `O CPF informado não pode ser utilizado para concluir o cadastro.${cpfDuplicateInDependents ? ' Você já tem um dependente com esse CPF.' : ''}`,
    }),
  gender: z.nativeEnum(Gender, {
    message: 'Selecione um gênero.',
  }),
});

export const editDependentFormSchema = z.object({
  legalName: z
    .string()
    .min(1, { message: 'Informe o nome completo.' })
    .max(130, { message: 'O nome deve ter no máximo 130 caracteres.' })
    .refine(completeNameValidator, {
      message: 'Informe o nome completo.',
    }),
  socialName: z
    .string()
    .max(130, { message: 'O nome social deve ter no máximo 130 caracteres.' })
    .optional(),
  birthday: z
    .string()
    .min(1, { message: 'Informe a data de nascimento.' })
    .refine(datePastValidator, {
      message: 'Informe uma data de nascimento válida.',
    }),
  gender: z.nativeEnum(Gender, {
    message: 'Selecione um gênero.',
  }),
});

export type CreateDependentForm = z.infer<ReturnType<typeof createDependentFormSchemaFactory>>;
export type EditDependentForm = z.infer<typeof editDependentFormSchema>;

const defaultValues: Partial<CreateDependentForm> = {
  legalName: '',
  socialName: '',
  birthday: '',
  documentNumber: '',
};

interface DependentFormProps {
  dependentId?: string;
  onSuccessCallback?: () => void;
  onBack?: () => void;
}

export const DependentForm = ({ dependentId, onSuccessCallback, onBack }: DependentFormProps) => {
  const { toast } = useToast();
  const router = useRouter();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const authenticatedUser = useStore(authStore, (state) => state.authenticatedUser);
  const analytics = getAnalyticsPlugin();

  const [showSocialName, setShowSocialName] = useState<boolean>(false);
  const [cpfExists, setCpfExists] = useState<boolean>(false);
  const [cpfDuplicateInDependents, setCpfDuplicateInDependents] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [showDiscardModal, setShowDiscardModal] = useState<boolean>(false);
  const [navigationResolver, setNavigationResolver] = useState<((value: boolean) => void) | null>(null);

  const createDependentFormSchema = useMemo(
    () => createDependentFormSchemaFactory(cpfExists, cpfDuplicateInDependents),
    [cpfExists, cpfDuplicateInDependents],
  );
  const resolver = dependentId ? editDependentFormSchema : createDependentFormSchema;

  const form = useForm<CreateDependentForm | EditDependentForm>({
    resolver: zodResolver(resolver),
    defaultValues,
    mode: 'onTouched',
    delayError: 100,
  });

  const handleInterceptAttempt = async (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setNavigationResolver(() => resolve);
      setShowDiscardModal(true);
    });
  };

  const { allowNavigation } = useInterceptNavigation({
    shouldIntercept: !!dependentId && form.formState.isDirty && !isLoading,
    onInterceptAttempt: handleInterceptAttempt,
  });

  useEffect(() => {
    if (dependentId) {
      analytics.track(AnalyticsEnum.DEPENDENTS_VIEW_EDIT_PAGE, {
        dependent_id: dependentId,
      });
    } else {
      analytics.track(AnalyticsEnum.DEPENDENTS_VIEW_NEW_PAGE);
    }
  }, [dependentId, analytics]);

  useEffect(() => {
    if (!authenticatedUser || !dependentId) {
      return;
    }

    const dependent = authenticatedUser?.dependents?.find((dependent: Dependent) => dependent.id === dependentId);

    if (!dependent) {
      return;
    }

    if (dependent.socialName) {
      setShowSocialName(true);
    }

    form.reset({
      documentNumber: dependent?.documentNumber,
      legalName: dependent?.legalName,
      socialName: dependent?.socialName || '',
      birthday: formatISO8601toBRDate(dependent?.birthday),
      gender: dependent?.gender,
    });
  }, [authenticatedUser, form, dependentId]);

  useEffect(() => {
    if (!dependentId && 'documentNumber' in form.formState.touchedFields && form.formState.touchedFields.documentNumber) {
      form.trigger('documentNumber');
    }
  }, [cpfExists, cpfDuplicateInDependents, form, dependentId]);

  const checkIfCpfAlreadyRegistered = async (cpf: string): Promise<void> => {
    setIsVerified(true);

    const cpfIsValid = cpfValidator(cpf);

    if (cpfIsValid) {
      const isDuplicate = isDependentCpfDuplicate(cpf, authenticatedUser?.dependents, dependentId);

      if (isDuplicate) {
        setCpfExists(true);
        setCpfDuplicateInDependents(true);
        return;
      }

      const cpfExists = await checkIfCpfExists(cpf);
      setCpfExists(cpfExists?.exists);
      setCpfDuplicateInDependents(false);
    }
  };

  const handleDocumentNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (...event: unknown[]) => void,
  ): void => {
    onChange(e);
    setCpfExists(false);
    setCpfDuplicateInDependents(false);
    setIsVerified(false);
    if (e.target.value) {
      form.trigger('documentNumber');
    }
  };

  const handleDocumentNumberBlur = async (e: React.FocusEvent<HTMLInputElement>, onBlur: () => void): Promise<void> => {
    onBlur();
    const cpfValue = e.target.value;
    const hasCpfError = !!form.formState.errors?.['documentNumber'];
    const hasValidValue = cpfValue && cpfValue.length === 14;

    if (!hasValidValue) {
      setCpfExists(false);
      setCpfDuplicateInDependents(false);
      setIsVerified(false);
      return;
    }

    if (!hasCpfError && hasValidValue && !isVerified) {
      await checkIfCpfAlreadyRegistered(cpfValue);
    }
  };

  const handleSubmitError = (error: unknown): void => {
    if (dependentId) {
      analytics.track(AnalyticsEnum.DEPENDENTS_EDIT_ERROR, {
        dependent_id: dependentId,
      });
    } else {
      analytics.track(AnalyticsEnum.DEPENDENTS_NEW_ERROR);
    }

    if (error instanceof HttpException) {
      if (error.status === 409) {
        toast.error('O CPF informado não pode ser utilizado para concluir o cadastro.');
      } else {
        toast.error('Erro ao salvar o dependente.');
      }
    } else {
      toast.error('Erro ao salvar o dependente.');
    }
  };

  const submitForm = async (data: CreateDependentForm | EditDependentForm): Promise<void> => {
    if (!authenticatedUser?.contact) {
      toast.error('Informações de contato do usuário autenticado não foram encontradas.');
      throw new Error('Missing contact information');
    }

    if (dependentId) {
      analytics.track(AnalyticsEnum.DEPENDENTS_EDIT_FORM_SUBMIT, {
        dependent_id: dependentId,
      });

      await updateDependent(dependentId, {
        ...data,
        birthday: formatBrDateToISO8601(data.birthday),
        contact: authenticatedUser?.contact,
      });

      analytics.track(AnalyticsEnum.DEPENDENTS_EDIT_SUCCESS, {
        dependent_id: dependentId,
      });
      toast.success('Dependente atualizado.');
    } else {
      analytics.track(AnalyticsEnum.DEPENDENTS_NEW_FORM_SUBMIT);

      const { birthday, ...formData } = data;

      await createDependent({
        ...formData,
        contact: authenticatedUser?.contact,
        birthday: formatBrDateToISO8601(birthday),
        documentType: DocumentType.CPF,
      });

      analytics.track(AnalyticsEnum.DEPENDENTS_NEW_SUCCESS);
      toast.success('Dependente cadastrado.');
    }

    await resolveAuthenticatedUser();
    allowNavigation();
  };

  const onSubmit = form.handleSubmit(async (data): Promise<void> => {
    startLoading();
    try {
      await submitForm(data);
      if (onSuccessCallback) {
        onSuccessCallback();
      } else {
        router.push('/dependentes');
      }
    } catch (error) {
      handleSubmitError(error);
    } finally {
      stopLoading();
    }
  });

  const handleDiscardConfirm = async (shouldDiscard: boolean): Promise<void> => {
    if (shouldDiscard) {
      allowNavigation();
      navigationResolver?.(true);
      setNavigationResolver(null);
      setShowDiscardModal(false);
    } else {
      await form.handleSubmit(
        async (data) => {
          try {
            await submitForm(data);
            navigationResolver?.(true);
            setNavigationResolver(null);
            setShowDiscardModal(false);
          } catch (error) {
            handleSubmitError(error);
            navigationResolver?.(false);
            setNavigationResolver(null);
            setShowDiscardModal(false);
          }
        },
        () => {
          navigationResolver?.(false);
          setNavigationResolver(null);
          setShowDiscardModal(false);
        },
      )();
    }
  };

  const handleModalOpenChange = (open: boolean): void => {
    if (!open && navigationResolver) {
      navigationResolver(false);
      setNavigationResolver(null);
    }
    setShowDiscardModal(open);
  };

  return (
    <Form {...form}>
      <form aria-label="form" className="flex flex-col gap-6" onSubmit={onSubmit}>
        <DiscardChangesModal
          open={showDiscardModal}
          setOpen={handleModalOpenChange}
          onConfirm={handleDiscardConfirm}
        />
        <FormField
          control={form.control}
          name="documentNumber"
          render={({ field }) => (
            <>
              <FormItem>
                <FormLabel disabled={!!dependentId}>
                  CPF
                </FormLabel>
                <FormControl>
                  <Input
                    data-testid={DataTestIdEnum.DEPENDENT_FORM_DOCUMENT_NUMBER_INPUT}
                    placeholder="000.000.000-00"
                    mask="999.999.999-99"
                    inputMode="numeric"
                    disabled={!!dependentId}
                    {...field}
                    onChange={(e) => handleDocumentNumberChange(e, field.onChange)}
                    onBlur={(e) => handleDocumentNumberBlur(e, field.onBlur)}
                    className="!text-clip"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </>
          )}
        />

        <FormField
          control={form.control}
          name="legalName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Nome (conforme o documento de identificação)
              </FormLabel>
              <FormControl>
                <Input
                  data-testid={DataTestIdEnum.DEPENDENT_FORM_LEGAL_NAME_INPUT}
                  mask={Array(256)
                    .fill([new RegExp('^[a-zA-ZÀ-ú ]+$')])
                    .flat()}
                  placeholder="Digite o nome completo"
                  maxLength={130}
                  {...field}
                  shouldClearValue
                  className="!text-clip"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!showSocialName && (
          <div className="flex justify-between">
            <div
              data-testid={DataTestIdEnum.DEPENDENT_FORM_INCLUDE_SOCIAL_NAME_BUTTON}
              className="flex gap-2 text-green-500 text-base hover:underline cursor-pointer"
              onClick={() => {
                setShowSocialName(true);
              }}
            >
              <div className="w-5 h-5 flex flex-center justify-center self-center">
                <Icon icon={faPlus} className="text-base self-center" />
              </div>
              <Typography
                className="font-semibold text-green-500"
              >
                Incluir nome social (opcional)
              </Typography>
            </div>

            <Tooltip
              message="Nome social é o nome pelo qual a pessoa prefere ser chamada, diferente do nome registrado em documentos."
            >
              <Icon
                icon={faCircleInfo}
                className="text-gray-500 w-auto cursor-pointer mt-1"
              />
            </Tooltip>
          </div>
        )}

        <FormField
          control={form.control}
          name="socialName"
          render={({ field }) => (
            <AnimatePresence initial>
              {showSocialName && (
                <motion.div
                  key="socialName"
                  initial={{ opacity: 0, height: 0, y: -80 }}
                  animate={{ opacity: 1, height: 'auto', y: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{
                    x: { type: 'keyframes', duration: 0.2 },
                  }}
                >
                  <FormItem>
                    <FormLabel>Nome social</FormLabel>
                    <FormControl>
                      <Input
                        data-testid={DataTestIdEnum.DEPENDENT_FORM_SOCIAL_NAME_INPUT}
                        placeholder="Digite o nome social (opcional)"
                        maxLength={130}
                        shouldClearValue
                        {...field}
                        className="!text-clip"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        />

        <FormField
          control={form.control}
          name="birthday"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Data de nascimento</FormLabel>
              <FormControl>
                <Input
                  data-testid={DataTestIdEnum.DEPENDENT_FORM_BIRTHDAY_INPUT}
                  placeholder="DD/MM/AAAA"
                  type="tel"
                  mask="99/99/9999"
                  shouldClearValue
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="gender"
          render={({ field }) => (
            <FormItem className="space-y-4">
              <FormLabel>Gênero</FormLabel>
              <FormControl>
                <RadioGroup
                  {...field}
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex flex-col gap-4"
                >
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem
                        data-testid={DataTestIdEnum.DEPENDENT_FORM_GENDER_FEMALE_RADIO}
                        value="FEMALE"
                        circleSize="xl"
                      />
                    </FormControl>
                    <FormLabel
                      className="font-normal hover:text-gray-900 text-base cursor-pointer pt-1"
                    >
                      Feminino
                    </FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem
                        data-testid={DataTestIdEnum.DEPENDENT_FORM_GENDER_MALE_RADIO}
                        value="MALE"
                        circleSize="xl"
                      />
                    </FormControl>
                    <FormLabel
                      className="font-normal hover:text-gray-900 text-base cursor-pointer pt-1"
                    >
                      Masculino
                    </FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem
                        data-testid={DataTestIdEnum.DEPENDENT_FORM_GENDER_NONBINARY_RADIO}
                        value="NONBINARY"
                        circleSize="xl"
                      />
                    </FormControl>
                    <FormLabel
                      className="font-normal hover:text-gray-900 text-base cursor-pointer pt-1"
                    >
                      Não binário
                    </FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem
                        data-testid={DataTestIdEnum.DEPENDENT_FORM_GENDER_NOT_INFORMED_RADIO}
                        value="NOT_INFORMED"
                        circleSize="xl"
                      />
                    </FormControl>
                    <FormLabel
                      className="font-normal hover:text-gray-900 text-base cursor-pointer pt-1"
                    >
                      Prefiro não informar
                    </FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-2">
          {onBack && (
            <Button
              data-testid={DataTestIdEnum.DEPENDENT_FORM_BACK_BUTTON}
              className="basis-1/4"
              type="reset"
              variant="secondary"
              onClick={onBack}
            >
              Voltar
            </Button>
          )}
          <Button
            data-testid={DataTestIdEnum.DEPENDENT_FORM_SUBMIT_BUTTON}
            type="submit"
            isLoading={isLoading}
            disabled={
              dependentId
                ? !form.formState.isValid || !form.formState.isDirty
                : !form.formState.isValid || cpfExists
            }
          >
            Salvar
          </Button>
        </div>
      </form>
    </Form>
  );
};
